import { BOULDER, DROWSE, GROUND, KENNEL, LIME, NIGHTMARES, SIEVE, STRUCTURES, WALL_CLASS } from '@somnhold/content';
import { blockingAtCell, cellCenter, cellId } from '../grid.ts';
import { DIRS, hexToWorld } from '../hex.ts';
import { dist2, iatan2, idiv, imin, mulBp, withinRange } from '../math/fixed.ts';
import type { BattleState, Bot, Enemy, Structure, Zone } from '../types.ts';
import { damageEnemy, enemiesNear, enemyCell, ev, isFlying, targetable } from './combat.ts';
import { stepToward } from './enemies.ts';
import { isBoss } from './towers.ts';
import { bigOne } from './panoply.ts';

/*
 * The saberes that fight (docs/SABERES.md §4.1, §4.2): the sieve, the boulder, the kennel's mastiffs, the lime cloud
 * and the drowsing tower. Nothing here runs per nightmare per tick: a death, a cell entered, a shot, a zone's pulse.
 */

/** The bastion's one standing structure of a kind (the sieve, the kennel), found once per structures list. */
// Keyed by the list and its length: a structure placed mid-fight (pushed on the same list) makes it look again.
const ONE = new WeakMap<Structure[], { n: number; m: Map<string, Structure | null> }>();
const oneOf = (s: BattleState, type: 'sieve' | 'kennel'): Structure | null => {
  let c = ONE.get(s.structures);
  if (!c || c.n !== s.structures.length) {
    c = { n: s.structures.length, m: new Map() };
    ONE.set(s.structures, c);
  }
  const m = c.m;
  if (!m.has(type)) m.set(type, s.structures.find((st) => st.type === type) ?? null);
  const st = m.get(type) ?? null;
  return st && !st.destroyed ? st : null;
};

/* ---------- the sieve ---------- */

/** A nightmare fell: within reach of a standing sieve, its dust is caught (the wave's result pays it, campaign side). */
export const sieveDied = (s: BattleState, e: Enemy): void => {
  if (s.mode !== 'wave') return;
  const st = oneOf(s, 'sieve');
  if (!st) return;
  const c = hexToWorld(st.q, st.r);
  if (withinRange(e.x, e.y, c.x, c.y, SIEVE.reach)) s.stats.sieved = (s.stats.sieved ?? 0) + SIEVE.perKill;
};

/** What the sieve caught this wave, if it still stands at the end (fallen, it's lost). */
export const sieveCatch = (s: BattleState): number => {
  const st = s.structures.find((x) => x.type === 'sieve');
  return st && !st.destroyed ? imin(SIEVE.cap, s.stats.sieved ?? 0) : 0;
};

/* ---------- the boulder ---------- */

/** The cells a boulder rolls down: from its own, toward `roll`, while the next isn't higher, to BOULDER.cells. */
const boulderLine = (s: BattleState, st: Structure): number[] => {
  const land = s.map.land;
  const R = s.map.radius;
  const d = DIRS[st.roll ?? 0] ?? DIRS[0]!;
  let q = st.q;
  let r = st.r;
  let at = cellId(R, q, r);
  const line = [at];
  for (let k = 0; k < BOULDER.cells; k++) {
    const nq = q + d.q;
    const nr = r + d.r;
    const next = cellId(R, nq, nr);
    if (next < 0) break;
    if (land && ((land.level[next] ?? 0) > (land.level[at] ?? 0) || land.kind[next] === GROUND.water)) break;
    if (blockingAtCell(s, next)) break;
    line.push(next);
    q = nq;
    r = nr;
    at = next;
  }
  return line;
};

/** The first cell below each standing boulder, so a nightmare stepping there sets it off too. */
const BELOW = new WeakMap<Structure[], { n: number; m: Map<number, Structure> }>();
const boulderBelow = (s: BattleState): Map<number, Structure> => {
  const c = BELOW.get(s.structures);
  if (c && c.n === s.structures.length) return c.m;
  const m = new Map<number, Structure>();
  for (const st of s.structures) {
    if (st.type !== 'boulder') continue;
    const d = DIRS[st.roll ?? 0] ?? DIRS[0]!;
    const id = cellId(s.map.radius, st.q + d.q, st.r + d.r);
    if (id >= 0 && !m.has(id)) m.set(id, st);
  }
  BELOW.set(s.structures, { n: s.structures.length, m });
  return m;
};

/** The boulder below this cell, if one is set and charged: a nightmare on foot stepping here lets it go. */
export const boulderAbove = (s: BattleState, cell: number): Structure | null => {
  const st = boulderBelow(s).get(cell);
  return st && st.charges > 0 ? st : null;
};

/** It goes: down its line, one blow for each nightmare on foot on it; then it lies there until it's raised again. */
export const rollBoulder = (s: BattleState, st: Structure): void => {
  if (st.charges <= 0) return;
  st.charges--;
  const line = boulderLine(s, st);
  const on = new Set(line);
  const dmg = mulBp(STRUCTURES.boulder.damage[st.level - 1] as number, s.rules?.builtBp ?? 10000);
  for (const e of s.enemies) {
    if (e.hp <= 0 || isFlying(e) || !targetable(s, e) || !on.has(enemyCell(s, e))) continue;
    damageEnemy(s, e, dmg, { pid: '', type: 'physical' });
  }
  const a = cellCenter(s.map.radius, line[0] as number);
  const b = cellCenter(s.map.radius, line[line.length - 1] as number);
  // Only its roll: the spent chock is drawn from its charges (no generic «destroyed» burst over the rolling stone).
  ev(s, { t: 'shot', from: st.id, to: 0, kind: 'boulder', x: a.x, y: a.y, tx: b.x, ty: b.y });
};

/* ---------- the kennel's mastiffs ---------- */

/** Into the wave, the kennel lets its dogs out: two, three from its third level (KENNEL.dogs). */
export const kennelTick = (s: BattleState): void => {
  if (s.tick !== KENNEL.at || s.mode !== 'wave') return;
  const st = oneOf(s, 'kennel');
  if (!st) return;
  const n = KENNEL.dogs[st.level - 1] ?? KENNEL.dogs[KENNEL.dogs.length - 1] ?? 2;
  const c = hexToWorld(st.q, st.r);
  s.bots ??= [];
  for (let i = 0; i < n; i++) {
    s.bots.push({
      id: s.nextId++,
      owner: 0,
      pid: '',
      kind: 'mastiff',
      x: c.x + (i - 1) * 250,
      y: c.y + 200,
      hp: KENNEL.hp,
      maxHp: KENNEL.hp,
      damage: KENNEL.damage,
      radius: KENNEL.radius,
      every: KENNEL.every,
      cd: 0,
      expires: 0x7fffffff,
      taunt: 0,
      aura: 0,
      auraBp: 0,
      facing: 0,
      anim: 'idle',
      animT: 0,
    });
  }
  ev(s, { t: 'boom', x: c.x, y: c.y, r: 600, kind: 'kennel' });
};

/** The prey a mastiff hunts: the nearest of the ones going round (WALL_CLASS skirt), else the nearest to the keep. */
const preyOf = (s: BattleState, b: Bot): Enemy | null => {
  let best: Enemy | null = null;
  let bd = 0x7fffffff;
  let fallback: Enemy | null = null;
  let fd = 0x7fffffff;
  for (const e of enemiesNear(s, 0, 0, KENNEL.hunt, (x) => targetable(s, x) && !isFlying(x) && x.kind !== 'boneWall' && x.kind !== 'dragon')) {
    if ((WALL_CLASS as Partial<Record<string, string>>)[e.kind] === 'skirt') {
      const d = dist2(e.x, e.y, b.x, b.y);
      if (d < bd || (d === bd && best && e.id < best.id)) {
        bd = d;
        best = e;
      }
    } else {
      const d = e.x * e.x + e.y * e.y;
      if (d < fd || (d === fd && fallback && e.id < fallback.id)) {
        fd = d;
        fallback = e;
      }
    }
  }
  return best ?? fallback;
};

/** A mastiff: runs to its prey (looked for again every KENNEL.retarget ticks), bites and holds it a moment. */
export const mastiffTick = (s: BattleState, b: Bot): void => {
  let prey = b.prey ? s.enemies.find((e) => e.id === b.prey && e.hp > 0) : undefined;
  if (!prey || b.animT % KENNEL.retarget === 0) {
    prey = preyOf(s, b) ?? undefined;
    b.prey = prey?.id;
  }
  if (b.cd > 0) b.cd--;
  if (!prey) {
    if (b.anim === 'walk') b.anim = 'idle';
    return;
  }
  if (!withinRange(b.x, b.y, prey.x, prey.y, idiv(b.radius * 3, 4))) {
    b.anim = 'walk';
    b.facing = iatan2(prey.y - b.y, prey.x - b.x);
    stepToward(b, prey.x, prey.y, KENNEL.step);
    return;
  }
  if (b.cd > 0) return;
  b.facing = iatan2(prey.y - b.y, prey.x - b.x);
  damageEnemy(s, prey, mulBp(b.damage, s.rules?.builtBp ?? 10000), { pid: '' });
  // It bites back: the dogs fall, and a long wave leaves the kennel empty (docs/SABERES.md §4.1).
  if (prey.hp > 0) b.hp -= mulBp(prey.damage, KENNEL.biteBackBp);
  b.cd = b.every;
  b.anim = 'attack';
  b.animT = 0;
  // Held a moment (the hound's rule): not the big, not bosses or the Unsleeping; warlords for half.
  const big = bigOne(prey) || !!NIGHTMARES[prey.kind].big || isBoss(prey) || s.bossId === prey.id || !!prey.un || prey.kind === 'warden' || prey.kind === 'magmaLord' || prey.kind === 'colossus';
  if (!big && prey.hp > 0 && (prey.pinCd ?? 0) <= s.tick) {
    const t = prey.elite ? idiv(KENNEL.pin, 2) : KENNEL.pin;
    prey.slowBp = 10000;
    if (prey.slowTicks < t) prey.slowTicks = t;
    prey.pinUntil = s.tick + t;
    prey.pinCd = s.tick + KENNEL.pinEvery;
    ev(s, { t: 'boom', x: prey.x, y: prey.y, r: 400, kind: 'houndPin' });
  }
};

/* ---------- the lime catapult ---------- */

/** Its cloud where the stone lands: one per tower, moved and refreshed by each shot (the pitch branch's way). */
export const limeCloud = (s: BattleState, st: Structure, x: number, y: number): void => {
  const z = st.burnZone ? s.zones.find((o) => o.id === st.burnZone) : undefined;
  if (z) {
    z.x = x;
    z.y = y;
    z.ticks = LIME.ticks;
    return;
  }
  const id = s.nextId++;
  s.zones.push({ id, kind: 'lime', x, y, radius: LIME.radius, cells: [], ticks: LIME.ticks, every: LIME.every, damage: 0, slowBp: 0, pid: '' });
  st.burnZone = id;
};

/** Whether a nightmare throws or shoots from afar (what the lime blinds). */
const shooter = (e: Enemy): boolean => {
  const d = NIGHTMARES[e.kind];
  return !!(d.lobs || d.hurls || d.shootsTowers || d.shootsHeroes);
};

/** A pulse of the cloud: the shooters on foot inside can't shoot until the next. */
export const limeTick = (s: BattleState, z: Zone): void => {
  if (z.ticks % z.every !== 0) return;
  for (const e of enemiesNear(s, z.x, z.y, z.radius, (o) => !isFlying(o) && shooter(o))) e.limeUntil = s.tick + z.every + 1;
};

/** Blinded by lime: it can't shoot or throw this tick. */
export const limed = (s: BattleState, e: Enemy): boolean => (e.limeUntil ?? 0) > s.tick;

/* ---------- the drowsing tower ---------- */

/** Whether this shot of a drowsing tower puts its target to sleep (one in DROWSE.every), counting it. */
export const drowseShot = (s: BattleState, st: Structure, e: Enemy): boolean => {
  // One it can't put to sleep doesn't count: that shot hurts as the others do.
  if (!sleepable(s, e)) return false;
  st.shots = ((st.shots ?? 0) + 1) % DROWSE.every;
  return st.shots === 0;
};

/** Whether a drowsing tower can put this one to sleep. */
const sleepable = (s: BattleState, e: Enemy): boolean => {
  const d = NIGHTMARES[e.kind];
  return !(isBoss(e) || s.bossId === e.id || e.elite || e.un || d.siege || e.kind === 'warden' || e.kind === 'magmaLord' || e.kind === 'colossus');
};

/**
 * Asleep (docs/SABERES.md §4.2): not the bosses, warlords, siege engines or the Unsleeping; the big a third as long.
 * Any harm wakes it (`damageEnemy` clears `drowse` and the stun), and a hero's blow on a sleeper is sure to be critical (it's stunned).
 */
export const putToSleep = (s: BattleState, e: Enemy): void => {
  if (!sleepable(s, e)) return;
  const t = NIGHTMARES[e.kind].big ? idiv(DROWSE.ticks, 3) : DROWSE.ticks;
  if (e.stun < t) e.stun = t;
  e.drowse = true;
  e.drowseUntil = s.tick + t;
  ev(s, { t: 'boom', x: e.x, y: e.y, r: 350, kind: 'drowse' });
};

